// Groww Excel Parser
// Excel Structure (with header rows):
// Row 1: Name, [Client Name]
// Row 2: Unique Client Code, [Code]
// Row 3: Order history for stocks from [date] to [date]
// Row 4: Stock name, Symbol, ISIN, Type, Quantity, Value, Exchange, Exchange Order Id, Execution date and time, Order status
// Row 5+: Data rows
// Note: Groww uses Excel format (.xlsx) instead of CSV

import * as XLSX from 'xlsx';

export interface GrowwTrade {
  stockName: string;
  symbol: string;
  isin: string;
  type: 'BUY' | 'SELL';
  quantity: number;
  value: number;
  exchange: string;
  orderId: string;
  executionDateTime: string;
  orderStatus: string;
}

export interface ParsedTranche {
  orderId: string;
  symbol: string;
  isin: string;
  type: 'buy' | 'sell';
  quantity: number;
  avgPrice: number;
  timestamp: string;
  exchange: string;
}

export interface ParsedPosition {
  symbol: string;
  isin: string;
  exchange: string;
  tranches: ParsedTranche[];
}

export interface ParseResult {
  positions: ParsedPosition[];
  rejectedRows: RejectedRow[];
}

export interface RejectedRow {
  rowNumber: number;
  symbol?: string;
  reason: string;
  rawData?: string;
}

/**
 * Parse Groww order history Excel file
 * Returns positions grouped by ISIN
 */
export async function parseGrowwExcel(file: File): Promise<ParseResult> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });
        
        // Get the first sheet
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        
        // Convert to JSON with header mapping
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { 
          header: 1,
          raw: false,
          defval: ''
        }) as string[][];
        
        const { trades, rejectedRows } = parseExcelData(jsonData);
        const positions = groupTradesIntoPositions(trades);
        resolve({ positions, rejectedRows });
      } catch (error) {
        console.error('Error parsing Groww Excel:', error);
        reject(error);
      }
    };
    
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsArrayBuffer(file);
  });
}

/**
 * Parse Excel data rows into trade objects
 */
function parseExcelData(rows: string[][]): { trades: GrowwTrade[]; rejectedRows: RejectedRow[] } {
  const trades: GrowwTrade[] = [];
  const rejectedRows: RejectedRow[] = [];
  
  if (rows.length < 2) {
    return { trades, rejectedRows };
  }
  
  // Find the header row by looking for "Stock name" or "Symbol" or "ISIN"
  let headerRowIndex = -1;
  for (let i = 0; i < Math.min(10, rows.length); i++) {
    const row = rows[i];
    if (row && row.some(cell => {
      const cellLower = (cell || '').toLowerCase().trim();
      return cellLower === 'stock name' || cellLower === 'symbol' || cellLower === 'isin';
    })) {
      headerRowIndex = i;
      break;
    }
  }
  
  if (headerRowIndex === -1) {
    console.error('Could not find header row in Groww Excel');
    // Try to parse assuming standard Groww format without headers
    headerRowIndex = findDataStartRow(rows);
  }
  
  const headerRow = rows[headerRowIndex] || [];
  
  // Map column indices
  const columnMap = findColumnIndices(headerRow);
  
  console.log('Groww Parser: Header row index:', headerRowIndex);
  console.log('Groww Parser: Header row:', headerRow);
  console.log('Groww Parser: Column mapping:', columnMap);
  
  // Parse data rows (skip header)
  for (let i = headerRowIndex + 1; i < rows.length; i++) {
    const row = rows[i];
    
    // Skip empty rows
    if (!row || row.length === 0 || row.every(cell => !cell || String(cell).trim() === '')) {
      continue;
    }
    
    try {
      const trade = parseGrowwRow(row, columnMap, i + 1);
      
      if (!trade) {
        continue;
      }
      
      // Validate required fields
      if (!trade.isin || trade.isin.trim() === '') {
        rejectedRows.push({
          rowNumber: i + 1,
          symbol: trade.symbol,
          reason: 'Missing ISIN',
          rawData: row.join(',')
        });
        continue;
      }
      
      // Only include executed orders
      const status = trade.orderStatus.toLowerCase();
      if (status !== 'executed' && status !== 'complete' && status !== 'completed') {
        rejectedRows.push({
          rowNumber: i + 1,
          symbol: trade.symbol,
          reason: `Order not executed (status: ${trade.orderStatus})`,
          rawData: row.join(',')
        });
        continue;
      }
      
      // Validate quantity
      if (trade.quantity <= 0) {
        rejectedRows.push({
          rowNumber: i + 1,
          symbol: trade.symbol,
          reason: 'Invalid quantity',
          rawData: row.join(',')
        });
        continue;
      }
      
      trades.push(trade);
    } catch (error) {
      rejectedRows.push({
        rowNumber: i + 1,
        reason: `Parse error: ${error}`,
        rawData: row.join(',')
      });
    }
  }
  
  console.log(`Groww Parser: Parsed ${trades.length} trades, rejected ${rejectedRows.length} rows`);
  return { trades, rejectedRows };
}

/**
 * Find the row where data starts (for files without clear headers)
 */
function findDataStartRow(rows: string[][]): number {
  for (let i = 0; i < rows.length; i++) {
    const row = rows[i];
    if (row && row.length >= 5) {
      // Check if this row has ISIN pattern (starts with INE)
      const hasIsin = row.some(cell => /^INE[A-Z0-9]+$/.test(String(cell).trim()));
      if (hasIsin) {
        return Math.max(0, i - 1); // Return previous row as header
      }
    }
  }
  return 0;
}

/**
 * Find column indices from header row
 * Groww columns: Stock name, Symbol, ISIN, Type, Quantity, Value, Exchange, Exchange Order Id, Execution date and time, Order status
 */
function findColumnIndices(headerRow: string[]): Record<string, number> {
  const map: Record<string, number> = {
    stockName: -1,
    symbol: -1,
    isin: -1,
    type: -1,
    quantity: -1,
    value: -1,
    exchange: -1,
    orderId: -1,
    executionDateTime: -1,
    orderStatus: -1
  };
  
  for (let i = 0; i < headerRow.length; i++) {
    const header = String(headerRow[i] || '').toLowerCase().trim();
    
    if (header === 'stock name' || header === 'stockname' || header === 'name') {
      if (map.stockName === -1) map.stockName = i;
    } else if (header === 'symbol' || header === 'scrip' || header === 'ticker') {
      map.symbol = i;
    } else if (header === 'isin') {
      map.isin = i;
    } else if (header === 'type' || header === 'transaction type' || header === 'side') {
      map.type = i;
    } else if (header === 'quantity' || header === 'qty' || header === 'units') {
      map.quantity = i;
    } else if (header === 'value' || header === 'amount' || header === 'total' || header === 'trade value') {
      map.value = i;
    } else if ((header === 'exchange' || header === 'exch') && map.exchange === -1) {
      map.exchange = i;
    } else if (header.includes('order id') || header === 'orderid' || header === 'order_id' || header === 'exchange order id') {
      map.orderId = i;
    } else if (header.includes('execution') || header.includes('date') || header.includes('time')) {
      if (map.executionDateTime === -1) map.executionDateTime = i;
    } else if (header === 'order status' || header === 'orderstatus' || header === 'status') {
      map.orderStatus = i;
    }
  }
  
  // If we couldn't find headers, try positional mapping for standard Groww format
  // Stock name(0), Symbol(1), ISIN(2), Type(3), Quantity(4), Value(5), Exchange(6), Exchange Order Id(7), Execution date and time(8), Order status(9)
  if (map.isin === -1 && headerRow.length >= 10) {
    console.log('Groww Parser: Using positional mapping');
    map.stockName = 0;
    map.symbol = 1;
    map.isin = 2;
    map.type = 3;
    map.quantity = 4;
    map.value = 5;
    map.exchange = 6;
    map.orderId = 7;
    map.executionDateTime = 8;
    map.orderStatus = 9;
  }
  
  return map;
}

/**
 * Parse a single row into a GrowwTrade object
 */
function parseGrowwRow(row: string[], columnMap: Record<string, number>, rowNumber: number): GrowwTrade | null {
  const getValue = (key: string): string => {
    const idx = columnMap[key];
    if (idx === -1 || idx >= row.length) return '';
    return (row[idx] || '').trim();
  };
  
  const stockName = getValue('stockName');
  const symbol = getValue('symbol');
  const isin = getValue('isin');
  const typeStr = getValue('type').toUpperCase();
  const quantityStr = getValue('quantity');
  const valueStr = getValue('value');
  const exchange = getValue('exchange');
  const orderId = getValue('orderId');
  const executionDateTime = getValue('executionDateTime');
  const orderStatus = getValue('orderStatus');
  
  // Skip if no symbol or isin
  if (!symbol && !isin) {
    return null;
  }
  
  // Parse type
  const type = typeStr === 'BUY' ? 'BUY' : typeStr === 'SELL' ? 'SELL' : null;
  if (!type) {
    return null;
  }
  
  // Parse quantity - handle various formats
  let quantity = 0;
  if (quantityStr) {
    // Remove commas and parse
    quantity = parseFloat(quantityStr.replace(/,/g, ''));
    if (isNaN(quantity)) quantity = 0;
  }
  
  // Parse value - handle various formats
  let value = 0;
  if (valueStr) {
    // Remove currency symbols and commas
    value = parseFloat(valueStr.replace(/[₹,]/g, '').trim());
    if (isNaN(value)) value = 0;
  }
  
  return {
    stockName,
    symbol,
    isin,
    type,
    quantity,
    value,
    exchange: exchange || 'NSE',
    orderId: orderId || `GROWW-${Date.now()}-${rowNumber}`,
    executionDateTime,
    orderStatus: orderStatus || 'Executed'
  };
}

/**
 * Group trades into positions by ISIN
 */
function groupTradesIntoPositions(trades: GrowwTrade[]): ParsedPosition[] {
  const positionMap = new Map<string, ParsedPosition>();
  
  for (const trade of trades) {
    const key = trade.isin;
    
    if (!positionMap.has(key)) {
      positionMap.set(key, {
        symbol: trade.symbol,
        isin: trade.isin,
        exchange: trade.exchange,
        tranches: []
      });
    }
    
    const position = positionMap.get(key)!;
    
    // Calculate average price from value if available
    let avgPrice = 0;
    if (trade.value > 0 && trade.quantity > 0) {
      avgPrice = trade.value / trade.quantity;
    }
    
    // Parse execution date/time
    const timestamp = parseGrowwDateTime(trade.executionDateTime);
    
    position.tranches.push({
      orderId: trade.orderId,
      symbol: trade.symbol,
      isin: trade.isin,
      type: trade.type.toLowerCase() as 'buy' | 'sell',
      quantity: trade.quantity,
      avgPrice,
      timestamp,
      exchange: trade.exchange
    });
  }
  
  // Sort tranches by timestamp within each position
  for (const position of positionMap.values()) {
    position.tranches.sort((a, b) => 
      new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
    );
  }
  
  return Array.from(positionMap.values());
}

/**
 * Parse Groww date/time format: "02-04-2025 03:23 PM" -> ISO string
 */
function parseGrowwDateTime(dateTimeStr: string): string {
  if (!dateTimeStr) {
    return new Date().toISOString();
  }
  
  try {
    // Format: DD-MM-YYYY HH:MM AM/PM
    const match = dateTimeStr.match(/(\d{2})-(\d{2})-(\d{4})\s+(\d{1,2}):(\d{2})\s*(AM|PM)?/i);
    
    if (match) {
      const [, day, month, year, hourStr, minute, ampm] = match;
      let hour = parseInt(hourStr, 10);
      
      // Convert to 24-hour format if AM/PM present
      if (ampm) {
        if (ampm.toUpperCase() === 'PM' && hour !== 12) {
          hour += 12;
        } else if (ampm.toUpperCase() === 'AM' && hour === 12) {
          hour = 0;
        }
      }
      
      const date = new Date(
        parseInt(year, 10),
        parseInt(month, 10) - 1,
        parseInt(day, 10),
        hour,
        parseInt(minute, 10)
      );
      
      return date.toISOString();
    }
    
    // Try direct parsing
    const date = new Date(dateTimeStr);
    if (!isNaN(date.getTime())) {
      return date.toISOString();
    }
  } catch (error) {
    console.warn('Failed to parse Groww date/time:', dateTimeStr);
  }
  
  return new Date().toISOString();
}

/**
 * Check if file is a valid Groww Excel file
 */
export function isValidGrowwFile(file: File): boolean {
  const validExtensions = ['.xlsx', '.xls'];
  const fileName = file.name.toLowerCase();
  
  return validExtensions.some(ext => fileName.endsWith(ext));
}
